import {snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import ReportingInfoTooltip from "../../components/ReportingInfoTooltip.jsx";
import {assignedMarketingChartPoint, partialProfitabilityChartPoint, selectProfitabilityMetric, selectReportProfitabilityMetric} from "../../utils/reportingPartialProfitability.js";
import ReportingCostCoverageContext from "../../components/reporting/ReportingCostCoverageContext.jsx";
import {calculateCompletePeriodPoas, poasUnavailableCopy, poasUnavailableReasons} from "../../utils/reportingMetricPresentation.js";
import {sourceData} from "../../utils/marketingTrendsPresentation.js";
import {isSnapshotDateUnavailable, snapshotHasPublishedDays, snapshotRowsForPublishedDays, withSnapshotMissingDays} from "../../utils/reportingSnapshotAvailability.js";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import {MarketingOrderCountMeta} from "./ReportingMarketingLab.jsx";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    formatChartMultiplier,
    formatDateLabel,
    KpiCard,
    moneyAmount,
    Panel,
    projectCurrency,
    currencyUnit,
    ReportingPageDescription,
    snapshotCompletenessAlert,
} from "./ReportingLab.jsx";

function sourceKey(source) {
    return source?.integrationName || "-";
}

function sourceLabel(source) {
    return source?.integrationName || source?.campaignName || "-";
}

export function marketingTrendsOrdersPanelCopy(t) {
    return {
        title: t("reporting_lab.marketing_trends.orders_by_campaigns", {defaultValue: "Objednávky přiřazené kampaním"}),
        help: t("reporting_lab.marketing_trends.orders_by_campaigns_help", {defaultValue: "Zobrazené objednávky jsou přiřazené zahrnutým kampaním podle ID kampaně; nejde o celkový počet objednávek obchodu."}),
    };
}

export function MarketingTrendsOrdersPanelTitle({t}) {
    const copy = marketingTrendsOrdersPanelCopy(t);

    return <span className="inline-flex items-center gap-2">
        {copy.title}
        <ReportingInfoTooltip>{copy.help}</ReportingInfoTooltip>
    </span>;
}

export function marketingTrendPnoValue(row) {
    return row?.pno ?? null;
}

function moneySourceData(source) {
    return source?.data ?? {};
}

function sumNumberSeriesByChannel(graphData, basis, labels, snapshot) {
    const channels = new Map();

    (graphData?.dataSources ?? []).forEach((source) => {
        const key = sourceKey(source);
        const existing = channels.get(key) ?? {
            key,
            label: sourceLabel(source),
            values: labels.map((label) => isSnapshotDateUnavailable(snapshot, label) ? null : 0),
            total: 0,
        };
        const data = sourceData(source, basis);

        labels.forEach((label, index) => {
            if (isSnapshotDateUnavailable(snapshot, label)) {
                existing.values[index] = null;
                return;
            }

            const value = Number(data[label] ?? 0);
            existing.values[index] += value;
            existing.total += value;
        });

        channels.set(key, existing);
    });

    return Array.from(channels.values()).sort((a, b) => b.total - a.total);
}

export function buildTopCampaignSeries(graphData, basis, labels, metricData = null, limit = 8, preserveUnavailable = false, snapshot = null) {
    const metricByCampaign = new Map((metricData?.dataSources ?? []).map((source) => [
        source.campaignId || source.campaignRemoteId || source.campaignName,
        source,
    ]));

    return (graphData?.dataSources ?? [])
        .map((source) => {
            const data = sourceData(source, basis);
            const metricSource = metricByCampaign.get(source.campaignId || source.campaignRemoteId || source.campaignName);
            const metricValues = sourceData(metricSource, basis);
            const total = labels.reduce((sum, label) => sum + (isSnapshotDateUnavailable(snapshot, label) ? 0 : Number(metricValues[label] ?? data[label] ?? 0)), 0);

            return {
                id: source.campaignId || source.campaignRemoteId || source.campaignName,
                label: source.campaignName || "-",
                channel: sourceLabel(source),
                campaignRemoteId: source.campaignRemoteId,
                values: labels.map((label) => isSnapshotDateUnavailable(snapshot, label) || (preserveUnavailable && data[label] == null) ? null : Number(data[label] ?? 0)),
                total,
            };
        })
        .filter((series) => series.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, limit)
        .map((series, _, selected) => {
            if (selected.filter((candidate) => candidate.label === series.label).length < 2) {
                return series;
            }

            const qualifier = [series.channel, series.campaignRemoteId || series.id].filter(Boolean).join(" · ");
            return {...series, label: `${series.label} (${qualifier})`};
        });
}

function weightedCpcByChannel(cpcData, clicksData, basis, labels, snapshot) {
    const clicksByCampaign = new Map((clicksData?.dataSources ?? []).map((source) => [
        source.campaignId || source.campaignRemoteId || source.campaignName,
        source,
    ]));
    const channels = new Map();

    (cpcData?.dataSources ?? []).forEach((source) => {
        const key = sourceKey(source);
        const existing = channels.get(key) ?? {
            key,
            label: sourceLabel(source),
            weighted: labels.map((label) => isSnapshotDateUnavailable(snapshot, label) ? null : 0),
            clicks: labels.map((label) => isSnapshotDateUnavailable(snapshot, label) ? null : 0),
            totalClicks: 0,
        };
        const cpc = moneySourceData(source);
        const clicks = sourceData(clicksByCampaign.get(source.campaignId || source.campaignRemoteId || source.campaignName), basis);

        labels.forEach((label, index) => {
            if (isSnapshotDateUnavailable(snapshot, label)) {
                existing.weighted[index] = null;
                existing.clicks[index] = null;
                return;
            }

            const clickCount = Number(clicks[label] ?? 0);
            const cpcMajor = moneyAmount(cpc[label]) / 100;

            existing.weighted[index] += cpcMajor * clickCount;
            existing.clicks[index] += clickCount;
            existing.totalClicks += clickCount;
        });

        channels.set(key, existing);
    });

    return Array.from(channels.values())
        .map((channel) => ({
            ...channel,
            values: labels.map((label, index) => isSnapshotDateUnavailable(snapshot, label) ? null : channel.clicks[index] > 0 ? channel.weighted[index] / channel.clicks[index] : null),
        }))
        .filter((channel) => channel.totalClicks > 0)
        .sort((a, b) => b.totalClicks - a.totalClicks);
}

function legendStyle() {
    return {
        bottom: 0,
        left: "center",
        type: "plain",
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };
}

function lineSeries(rows, type = "line") {
    return rows.map((row, index) => ({
        name: row.label,
        type,
        stack: type === "bar" ? "total" : undefined,
        smooth: type === "line",
        symbolSize: type === "line" ? 6 : undefined,
        barMaxWidth: type === "bar" ? 36 : undefined,
        data: row.values,
        itemStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
        lineStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
    }));
}

export function trendPoasPresentation(row, snapshotIncomplete = false) {
    const partialProfitability = row?.partialProfitabilityMarketing;
    const standardSelection = selectProfitabilityMetric({fullValue: row?.poas, fullComplete: row?.purchaseCostCompleteGross === true, partialProfitability, partialField: "knownPoas"});
    return selectReportProfitabilityMetric({standardSelection, partialProfitability, assignedField: "poas", snapshotIncomplete});
}

export default function ReportingMarketingTrendsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData(null);

        const api = new ErpCalculationApi(getAuthorityConfiguration());

        api.reportingMarketingTrends({projectId: project.id, erpFilter})
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const snapshot = data?.snapshot;
    const summaryUnavailable = !snapshotHasPublishedDays(snapshot);
    const labels = withSnapshotMissingDays(snapshot, data?.orders?.labels ?? data?.clicks?.labels ?? data?.pno?.labels ?? data?.cpc?.labels ?? []);
    const xLabels = labels.map(formatDateLabel);
    const currency = data?.cpc?.dataSources?.find((source) => Object.values(source.data ?? {})[0]?.currency)?.data
        ? Object.values(data.cpc.dataSources.find((source) => Object.values(source.data ?? {})[0]?.currency).data ?? {})[0]?.currency
        : projectCurrency(project);
    const ordersByChannel = useMemo(() => snapshotRowsForPublishedDays(sumNumberSeriesByChannel(data?.orders, "marketing", labels, snapshot).slice(0, 8), snapshot), [data, labels.join("|")]);
    const clicksByChannel = useMemo(() => snapshotRowsForPublishedDays(sumNumberSeriesByChannel(data?.clicks, "marketing", labels, snapshot).slice(0, 8), snapshot), [data, labels.join("|")]);
    const cpcByChannel = useMemo(() => snapshotRowsForPublishedDays(weightedCpcByChannel(data?.cpc, data?.clicks, "marketing", labels, snapshot).slice(0, 8), snapshot), [data, labels.join("|")]);
    const pnoTopCampaigns = useMemo(() => snapshotRowsForPublishedDays(buildTopCampaignSeries(data?.pno, "marketing", labels, data?.orders, 8, true, snapshot), snapshot), [data, labels.join("|")]);
    const totalRows = data?.totals?.rows ?? [];
    const hasProfitabilityData = totalRows.some((row) => row.poas !== undefined || row.partialProfitabilityMarketing?.knownPoas !== undefined || row.partialProfitabilityMarketing?.assignedMarketingResult?.poas !== undefined);
    const completeTotals = totalRows.reduce((sum, row) => {
        sum.spend += moneyAmount(row.spend);
        sum.turnover += moneyAmount(row.turnover);
        sum.orders += Number(row.orders ?? 0);
        sum.clicks += Number(row.clicks ?? 0);
        sum.impressions += Number(row.impressions ?? 0);
        return sum;
    }, {spend: 0, turnover: 0, orders: 0, clicks: 0, impressions: 0});
    const marketingPartialProfitability = data?.totals?.partialProfitabilityMarketing;
    const fullPoas = summaryUnavailable ? null : calculateCompletePeriodPoas(totalRows, "gross");
    const standardPoasPresentation = selectProfitabilityMetric({fullValue: hasProfitabilityData ? fullPoas : undefined, fullComplete: totalRows.length > 0 && totalRows.every((row) => row.purchaseCostCompleteGross === true), partialProfitability: summaryUnavailable ? undefined : marketingPartialProfitability, partialField: "knownPoas"});
    const poasPresentation = selectReportProfitabilityMetric({standardSelection: standardPoasPresentation, partialProfitability: marketingPartialProfitability, assignedField: "poas", snapshotIncomplete: summaryUnavailable});
    const completePoas = poasPresentation.value ?? null;
    const poasReasons = summaryUnavailable ? [] : poasUnavailableReasons(totalRows, {
        revenue: "turnover",
        spend: "spend",
        costComplete: "purchaseCostCompleteGross",
        costUnknown: "purchaseCostUnknownGross",
        costEstimated: "purchaseCostEstimatedGross",
    }, completePoas === null);
    const poasUnavailable = poasUnavailableCopy(poasReasons, t);
    const showPoasCard = hasProfitabilityData || (!summaryUnavailable && poasReasons.includes("incompleteCosts"));
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const grid = {left: 14, right: 18, top: 28, bottom: 58, containLabel: true};

    const ordersOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid,
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: legendStyle(),
        xAxis: {type: "category", data: xLabels},
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartNumber}},
        series: lineSeries(ordersByChannel, "bar"),
    };

    const clicksOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid,
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: legendStyle(),
        xAxis: {type: "category", data: xLabels},
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartNumber}},
        series: lineSeries(clicksByChannel, "bar"),
    };

    const pnoOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid,
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, formatChartPercent),
        },
        legend: legendStyle(),
        xAxis: {type: "category", data: xLabels},
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartPercent}},
        series: lineSeries(pnoTopCampaigns),
    };

    const cpcOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid,
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: legendStyle(),
        xAxis: {type: "category", data: xLabels},
        yAxis: {
            type: "value",
            name: currencyUnit(currency),
            nameTextStyle: chartTextStyle,
            axisLabel: {fontSize: 12, formatter: (value) => formatChartCurrency(value, currency)},
        },
        series: lineSeries(cpcByChannel),
    };

    const financialTrendOption = {
        color: ["#7c3aed", "#2563eb"],
        textStyle: chartTextStyle,
        grid,
        tooltip: {trigger: "axis", formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency))},
        legend: legendStyle(),
        xAxis: {type: "category", data: labels.map(formatDateLabel)},
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartNumber}},
        series: [
            {name: t("reporting_lab.table.marketing", {defaultValue: "Marketing"}), type: "bar", data: labels.map((date) => { const row = totalRows.find((item) => item.date === date); return isSnapshotDateUnavailable(snapshot, date) ? null : row ? moneyAmount(row.spend) / 100 : null; }), barMaxWidth: 30},
            {name: t("reporting_lab.marketing_trends.order_performance_turnover", {defaultValue: "Obrat objednávek kromě stornovaných"}), type: "line", smooth: true, data: labels.map((date) => { const row = totalRows.find((item) => item.date === date); return isSnapshotDateUnavailable(snapshot, date) ? null : row ? moneyAmount(row.turnover) / 100 : null; })},
        ].filter((series) => hasProfitabilityData || series.name !== "POAS"),
    };

    const efficiencyTrendOption = {
        color: ["#f97316", "#16a34a", "#0ea5e9", "#e11d48"],
        textStyle: chartTextStyle,
        grid,
        tooltip: {trigger: "axis", formatter: (params) => formatAxisTooltip(params, [(value) => formatChartPercent(value, 2), formatChartMultiplier, formatChartPercent, formatChartPercent])},
        legend: legendStyle(),
        xAxis: {type: "category", data: labels.map(formatDateLabel)},
        yAxis: [
            {type: "value", axisLabel: {fontSize: 12, formatter: formatChartPercent}},
            {type: "value", axisLabel: {fontSize: 12, formatter: formatChartMultiplier}},
        ],
        series: [
            {name: "PNO", type: "line", smooth: true, data: labels.map((date) => { const row = totalRows.find((item) => item.date === date); return isSnapshotDateUnavailable(snapshot, date) ? null : marketingTrendPnoValue(row); })},
            {name: "POAS", type: "line", smooth: true, yAxisIndex: 1, data: labels.map((date) => { const row = totalRows.find((item) => item.date === date); const unavailable = isSnapshotDateUnavailable(snapshot, date); const selected = trendPoasPresentation(row, unavailable); return unavailable ? null : selected.assignedMarketing ? assignedMarketingChartPoint(selected.value ?? null, row?.partialProfitabilityMarketing, t, "poas") : partialProfitabilityChartPoint(selected.value ?? null, row?.partialProfitabilityMarketing, selected.partial, t); })},
            {name: "CTR", type: "line", smooth: true, data: labels.map((date) => { const row = totalRows.find((item) => item.date === date); return isSnapshotDateUnavailable(snapshot, date) ? null : row?.ctr ?? null; })},
            {name: t("reporting_lab.table.conversion_rate", {defaultValue: "Konverze"}), type: "line", smooth: true, data: labels.map((date) => { const row = totalRows.find((item) => item.date === date); return isSnapshotDateUnavailable(snapshot, date) ? null : row?.conversionRate ?? null; })},
        ].filter((series) => hasProfitabilityData || series.name !== "POAS"),
    };

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.marketing_trends", {defaultValue: "Marketingové trendy"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(data?.snapshot, t)]}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.marketing_trends.title", {defaultValue: "K čemu slouží marketingové trendy"})}
                >
                    {t("reporting_lab.descriptions.marketing_trends.text", {defaultValue: "Sleduje objednávky kromě stornovaných a vývoj marketingových nákladů, kliků a CPC. PNO, ROAS a POAS používají stejnou objednávkovou bázi."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">
                <ReportingSnapshotStatus snapshots={snapshot}/>
                <ReportingCostCoverageContext data={marketingPartialProfitability} reportContext="marketing_trends" loading={loading}/>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.marketing.costs", {defaultValue: "Marketingové náklady"})} value={summaryUnavailable ? "—" : renderCurrencyValue({amount: completeTotals.spend, currency}, true)} meta={summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : t("reporting_lab.marketing_trends.complete_scope", {defaultValue: "Všechny měřené marketingové kanály"})}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.revenue", {defaultValue: "Obrat"})} value={summaryUnavailable ? "—" : renderCurrencyValue({amount: completeTotals.turnover, currency}, true)} meta={summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : <MarketingOrderCountMeta count={completeTotals.orders} renderNumber={renderNumber} t={t}/>} info={t("reporting_lab.marketing_trends.order_performance_turnover", {defaultValue: "Obrat objednávek kromě stornovaných"})} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
                </div>
                {showPoasCard && <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label="POAS" value={completePoas === null ? "-" : formatChartMultiplier(completePoas)} meta={<>
                        <span className="block">{t("reporting_lab.marketing.poas_basis_short", {defaultValue: "Hrubý zisk před reklamou / marketingové náklady"})}{summaryUnavailable ? ` · ${snapshotUnavailableShort(snapshot, t)}` : !poasPresentation.partial && poasUnavailable.short ? ` · ${poasUnavailable.short}` : ""}</span>
                    </>} metaTone={completePoas === null && poasUnavailable.short ? "unavailable" : "default"} info={<>
                        <div>{t("reporting_lab.metrics_help.poas", {defaultValue: "Hrubý zisk dělený náklady na reklamu. POAS je násobek; při nulových nákladech na reklamu není definován."})}</div>
                        {poasPresentation.partial && <div className="mt-1">{t("reporting_lab.partial_profitability.help", {defaultValue: "Výsledek vychází z řádků se známou tržbou a nákupním nákladem. Od jejich hrubého zisku se odečítají všechny reklamní náklady stejného výběru."})}</div>}
                        {summaryUnavailable && <div className="mt-1">{t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny a srovnání za celé období nejsou dostupné. Grafy a dílčí rozklady zahrnují pouze publikované dny."})}</div>}
                        {!poasPresentation.partial && poasUnavailable.detail && <div className="mt-1">{poasUnavailable.detail}</div>}
                    </>} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
                </div>}
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.table.impressions", {defaultValue: "Imprese"})} value={summaryUnavailable ? "—" : renderNumber(completeTotals.impressions)} meta={summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : `${t("reporting_lab.marketing_trends.clicks", {defaultValue: "Kliky"})}: ${renderNumber(completeTotals.clicks)}`}/>
                </div>

                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.spend_revenue_trend", {defaultValue: "Marketingové náklady a obrat v čase"})}>
                        <EChart option={financialTrendOption} height={360}/>
                    </Panel>
                </div>
                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.efficiency_trend", {defaultValue: "Vývoj efektivity marketingu"})}>
                        <EChart option={efficiencyTrendOption} height={360}/>
                    </Panel>
                </div>

                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={<MarketingTrendsOrdersPanelTitle t={t}/> }>
                        <EChart option={ordersOption} height={360}/>
                    </Panel>
                </div>
                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.clicks_by_channel", {defaultValue: "Kliky podle kanálu"})}>
                        <EChart option={clicksOption} height={360}/>
                    </Panel>
                </div>
                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.pno_top_campaigns", {defaultValue: "PNO top 8 kampaní · bez storen"})}>
                        <EChart option={pnoOption} height={360}/>
                    </Panel>
                </div>
                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.cpc_by_channel", {defaultValue: "CPC podle kanálu"})}>
                        <EChart option={cpcOption} height={360}/>
                    </Panel>
                </div>
            </div>
    </WideSecureLayout>;
}
